/**
 * WhitepaperPage
 *
 * Full whitepaper for the ASGARD token - AsgardStudio's utility token.
 * Professional documentation explaining tokenomics, use cases, and mechanics.
 */

import { useState } from 'react';
import {
  ArrowLeft,
  Coins,
  Flame,
  ShoppingCart,
  Wallet,
  BarChart3,
  Shield,
  Zap,
  Globe,
  ChevronDown,
  CheckCircle2,
  XCircle,
  ArrowRight,
  FileText,
} from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';

// ============================================================================
// Types
// ============================================================================

interface WhitepaperPageProps {
  onNavigateHome: () => void;
}

// ============================================================================
// Main Component
// ============================================================================

export function WhitepaperPage({ onNavigateHome }: WhitepaperPageProps) {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gradient-to-b from-secondary-50 to-white dark:from-secondary-900 dark:to-secondary-800">
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Back Button */}
        <button
          onClick={onNavigateHome}
          className="inline-flex items-center gap-2 text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>{t('whitepaper.backToApp')}</span>
        </button>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl mb-6 shadow-lg shadow-primary-500/25">
            <FileText className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-secondary-900 dark:text-white mb-4">
            {t('whitepaper.title')}
          </h1>
          <p className="text-xl text-secondary-600 dark:text-secondary-400">
            {t('whitepaper.subtitle')}
          </p>
          <div className="mt-4 text-sm text-secondary-500 dark:text-secondary-500">
            {t('whitepaper.version')} 1.0 | {t('whitepaper.lastUpdated')}:{' '}
            {new Date().toLocaleDateString()}
          </div>
        </div>

        {/* Table of Contents */}
        <TableOfContents />

        {/* Sections */}
        <div className="space-y-16">
          <IntroductionSection />
          <ProblemSection />
          <SolutionSection />
          <TokenomicsSection />
          <HowItWorksSection />
          <OrderBookSection />
          <UseCasesSection />
          <RoadmapSection />
          <FAQSection />
          <DisclaimerSection />
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-secondary-200 dark:border-secondary-700 text-center">
          <p className="text-secondary-500 dark:text-secondary-400 text-sm">
            {t('whitepaper.footer')}
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Table of Contents
// ============================================================================

function TableOfContents() {
  const { t } = useTranslation();

  const sections = [
    { id: 'introduction', label: t('whitepaper.toc.introduction') },
    { id: 'problem', label: t('whitepaper.toc.problem') },
    { id: 'solution', label: t('whitepaper.toc.solution') },
    { id: 'tokenomics', label: t('whitepaper.toc.tokenomics') },
    { id: 'how-it-works', label: t('whitepaper.toc.howItWorks') },
    { id: 'order-book', label: t('whitepaper.toc.orderBook') },
    { id: 'use-cases', label: t('whitepaper.toc.useCases') },
    { id: 'roadmap', label: t('whitepaper.toc.roadmap') },
    { id: 'faq', label: t('whitepaper.toc.faq') },
  ];

  return (
    <div className="bg-white dark:bg-secondary-800 rounded-2xl p-6 mb-12 shadow-lg border border-secondary-200 dark:border-secondary-700">
      <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
        {t('whitepaper.tableOfContents')}
      </h2>
      <nav className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {sections.map((section, index) => (
          <a
            key={section.id}
            href={`#${section.id}`}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-secondary-600 dark:text-secondary-400 hover:bg-secondary-100 dark:hover:bg-secondary-700 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            <span className="text-sm font-medium text-primary-500">{index + 1}.</span>
            <span>{section.label}</span>
          </a>
        ))}
      </nav>
    </div>
  );
}

// ============================================================================
// Section Components
// ============================================================================

function IntroductionSection() {
  const { t } = useTranslation();

  return (
    <section id="introduction">
      <SectionHeader
        icon={<Coins className="h-6 w-6" />}
        title={t('whitepaper.introduction.title')}
      />
      <div className="prose prose-secondary dark:prose-invert max-w-none">
        <p className="text-lg text-secondary-700 dark:text-secondary-300 leading-relaxed">
          {t('whitepaper.introduction.p1')}
        </p>
        <p className="text-secondary-600 dark:text-secondary-400 leading-relaxed">
          {t('whitepaper.introduction.p2')}
        </p>

        {/* Key Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 not-prose">
          <StatCard label={t('whitepaper.introduction.stats.blockchain')} value="Solana" />
          <StatCard label={t('whitepaper.introduction.stats.type')} value="SPL Token" />
          <StatCard
            label={t('whitepaper.introduction.stats.supply')}
            value={t('whitepaper.introduction.stats.unlimited')}
          />
          <StatCard
            label={t('whitepaper.introduction.stats.burn')}
            value={t('whitepaper.introduction.stats.onUse')}
          />
        </div>
      </div>
    </section>
  );
}

function ProblemSection() {
  const { t } = useTranslation();

  const problems = [
    {
      icon: <XCircle className="h-5 w-5 text-red-500" />,
      title: t('whitepaper.problem.item1.title'),
      description: t('whitepaper.problem.item1.description'),
    },
    {
      icon: <XCircle className="h-5 w-5 text-red-500" />,
      title: t('whitepaper.problem.item2.title'),
      description: t('whitepaper.problem.item2.description'),
    },
    {
      icon: <XCircle className="h-5 w-5 text-red-500" />,
      title: t('whitepaper.problem.item3.title'),
      description: t('whitepaper.problem.item3.description'),
    },
    {
      icon: <XCircle className="h-5 w-5 text-red-500" />,
      title: t('whitepaper.problem.item4.title'),
      description: t('whitepaper.problem.item4.description'),
    },
  ];

  return (
    <section id="problem">
      <SectionHeader icon={<Shield className="h-6 w-6" />} title={t('whitepaper.problem.title')} />
      <p className="text-secondary-600 dark:text-secondary-400 mb-6">
        {t('whitepaper.problem.intro')}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {problems.map((problem) => (
          <div
            key={problem.title}
            className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              {problem.icon}
              <h4 className="font-semibold text-secondary-900 dark:text-white">{problem.title}</h4>
            </div>
            <p className="text-sm text-secondary-600 dark:text-secondary-400">
              {problem.description}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function SolutionSection() {
  const { t } = useTranslation();

  const solutions = [
    {
      icon: <CheckCircle2 className="h-5 w-5 text-green-500" />,
      title: t('whitepaper.solution.item1.title'),
      description: t('whitepaper.solution.item1.description'),
    },
    {
      icon: <CheckCircle2 className="h-5 w-5 text-green-500" />,
      title: t('whitepaper.solution.item2.title'),
      description: t('whitepaper.solution.item2.description'),
    },
    {
      icon: <CheckCircle2 className="h-5 w-5 text-green-500" />,
      title: t('whitepaper.solution.item3.title'),
      description: t('whitepaper.solution.item3.description'),
    },
    {
      icon: <CheckCircle2 className="h-5 w-5 text-green-500" />,
      title: t('whitepaper.solution.item4.title'),
      description: t('whitepaper.solution.item4.description'),
    },
  ];

  return (
    <section id="solution">
      <SectionHeader icon={<Zap className="h-6 w-6" />} title={t('whitepaper.solution.title')} />
      <p className="text-secondary-600 dark:text-secondary-400 mb-6">
        {t('whitepaper.solution.intro')}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {solutions.map((solution) => (
          <div
            key={solution.title}
            className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800/30 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              {solution.icon}
              <h4 className="font-semibold text-secondary-900 dark:text-white">{solution.title}</h4>
            </div>
            <p className="text-sm text-secondary-600 dark:text-secondary-400">
              {solution.description}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function TokenomicsSection() {
  const { t } = useTranslation();

  return (
    <section id="tokenomics">
      <SectionHeader
        icon={<BarChart3 className="h-6 w-6" />}
        title={t('whitepaper.tokenomics.title')}
      />

      {/* Token Info Card */}
      <div className="bg-gradient-to-br from-primary-500/10 to-primary-600/5 dark:from-primary-500/20 dark:to-primary-600/10 border border-primary-200 dark:border-primary-800/30 rounded-2xl p-6 mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <h4 className="label-muted">{t('whitepaper.tokenomics.tokenName')}</h4>
            <p className="text-2xl font-bold text-secondary-900 dark:text-white">AsgardStudio</p>
          </div>
          <div>
            <h4 className="label-muted">{t('whitepaper.tokenomics.ticker')}</h4>
            <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">$ASGARD</p>
          </div>
          <div>
            <h4 className="label-muted">{t('whitepaper.tokenomics.initialPrice')}</h4>
            <p className="text-2xl font-bold text-secondary-900 dark:text-white">$0.0007 USD</p>
          </div>
          <div>
            <h4 className="label-muted">{t('whitepaper.tokenomics.supply')}</h4>
            <p className="text-2xl font-bold text-secondary-900 dark:text-white">
              {t('whitepaper.tokenomics.supplyValue')}
            </p>
          </div>
        </div>
      </div>

      {/* Key Points */}
      <div className="space-y-4">
        <TokenomicsPoint
          icon={<Flame className="h-5 w-5 text-orange-500" />}
          title={t('whitepaper.tokenomics.burn.title')}
          description={t('whitepaper.tokenomics.burn.description')}
        />
        <TokenomicsPoint
          icon={<Coins className="h-5 w-5 text-primary-500" />}
          title={t('whitepaper.tokenomics.mint.title')}
          description={t('whitepaper.tokenomics.mint.description')}
        />
        <TokenomicsPoint
          icon={<BarChart3 className="h-5 w-5 text-green-500" />}
          title={t('whitepaper.tokenomics.price.title')}
          description={t('whitepaper.tokenomics.price.description')}
        />
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const { t } = useTranslation();

  const steps = [
    {
      number: '1',
      title: t('whitepaper.howItWorks.step1.title'),
      description: t('whitepaper.howItWorks.step1.description'),
      icon: <Wallet className="h-6 w-6" />,
    },
    {
      number: '2',
      title: t('whitepaper.howItWorks.step2.title'),
      description: t('whitepaper.howItWorks.step2.description'),
      icon: <ShoppingCart className="h-6 w-6" />,
    },
    {
      number: '3',
      title: t('whitepaper.howItWorks.step3.title'),
      description: t('whitepaper.howItWorks.step3.description'),
      icon: <Zap className="h-6 w-6" />,
    },
    {
      number: '4',
      title: t('whitepaper.howItWorks.step4.title'),
      description: t('whitepaper.howItWorks.step4.description'),
      icon: <Flame className="h-6 w-6" />,
    },
  ];

  return (
    <section id="how-it-works">
      <SectionHeader icon={<Zap className="h-6 w-6" />} title={t('whitepaper.howItWorks.title')} />

      <div className="relative">
        {/* Connection line */}
        <div className="absolute left-6 top-12 bottom-12 w-0.5 bg-gradient-to-b from-primary-500 via-primary-400 to-primary-500 hidden sm:block" />

        <div className="space-y-6">
          {steps.map((step) => (
            <div key={step.number} className="flex gap-4 sm:gap-6">
              {/* Step number */}
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary-500 text-white flex items-center justify-center font-bold text-lg shadow-lg shadow-primary-500/25 z-10">
                {step.number}
              </div>

              {/* Content */}
              <div className="flex-1 bg-white dark:bg-secondary-800 rounded-xl p-4 sm:p-5 border border-secondary-200 dark:border-secondary-700 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="text-primary-500">{step.icon}</div>
                  <h4 className="font-semibold text-secondary-900 dark:text-white">{step.title}</h4>
                </div>
                <p className="text-secondary-600 dark:text-secondary-400">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OrderBookSection() {
  const { t } = useTranslation();

  return (
    <section id="order-book">
      <SectionHeader
        icon={<BarChart3 className="h-6 w-6" />}
        title={t('whitepaper.orderBook.title')}
      />

      <p className="text-secondary-600 dark:text-secondary-400 mb-6">
        {t('whitepaper.orderBook.intro')}
      </p>

      {/* Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* AMM */}
        <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <XCircle className="h-5 w-5 text-red-500" />
            <h4 className="font-semibold text-secondary-900 dark:text-white">
              AMM (Raydium, Orca)
            </h4>
          </div>
          <ul className="space-y-2 text-sm text-secondary-600 dark:text-secondary-400">
            <li className="flex items-start gap-2">
              <XCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
              {t('whitepaper.orderBook.amm.item1')}
            </li>
            <li className="flex items-start gap-2">
              <XCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
              {t('whitepaper.orderBook.amm.item2')}
            </li>
            <li className="flex items-start gap-2">
              <XCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
              {t('whitepaper.orderBook.amm.item3')}
            </li>
          </ul>
        </div>

        {/* Order Book */}
        <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800/30 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            <h4 className="font-semibold text-secondary-900 dark:text-white">
              Order Book (OpenBook)
            </h4>
          </div>
          <ul className="space-y-2 text-sm text-secondary-600 dark:text-secondary-400">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
              {t('whitepaper.orderBook.ob.item1')}
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
              {t('whitepaper.orderBook.ob.item2')}
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
              {t('whitepaper.orderBook.ob.item3')}
            </li>
          </ul>
        </div>
      </div>

      <p className="text-secondary-600 dark:text-secondary-400 bg-secondary-100 dark:bg-secondary-800 p-4 rounded-lg">
        <strong className="text-secondary-900 dark:text-white">
          {t('whitepaper.orderBook.whyTitle')}
        </strong>{' '}
        {t('whitepaper.orderBook.whyDescription')}
      </p>
    </section>
  );
}

function UseCasesSection() {
  const { t } = useTranslation();

  const useCases = [
    {
      icon: '🎬',
      title: t('whitepaper.useCases.video.title'),
      description: t('whitepaper.useCases.video.description'),
    },
    {
      icon: '🎙️',
      title: t('whitepaper.useCases.audio.title'),
      description: t('whitepaper.useCases.audio.description'),
    },
    {
      icon: '🌍',
      title: t('whitepaper.useCases.dubbing.title'),
      description: t('whitepaper.useCases.dubbing.description'),
    },
    {
      icon: '🖼️',
      title: t('whitepaper.useCases.image.title'),
      description: t('whitepaper.useCases.image.description'),
    },
    {
      icon: '🗣️',
      title: t('whitepaper.useCases.voice.title'),
      description: t('whitepaper.useCases.voice.description'),
    },
    {
      icon: '💬',
      title: t('whitepaper.useCases.chat.title'),
      description: t('whitepaper.useCases.chat.description'),
    },
  ];

  return (
    <section id="use-cases">
      <SectionHeader icon={<Globe className="h-6 w-6" />} title={t('whitepaper.useCases.title')} />

      <p className="text-secondary-600 dark:text-secondary-400 mb-6">
        {t('whitepaper.useCases.intro')}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {useCases.map((useCase) => (
          <div
            key={useCase.title}
            className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-xl p-4 hover:shadow-lg transition-shadow"
          >
            <div className="text-3xl mb-3">{useCase.icon}</div>
            <h4 className="font-semibold text-secondary-900 dark:text-white mb-2">
              {useCase.title}
            </h4>
            <p className="text-sm text-secondary-600 dark:text-secondary-400">
              {useCase.description}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function RoadmapSection() {
  const { t } = useTranslation();

  const phases = [
    {
      phase: 'Q1 2026',
      title: t('whitepaper.roadmap.phase1.title'),
      items: [
        t('whitepaper.roadmap.phase1.item1'),
        t('whitepaper.roadmap.phase1.item2'),
        t('whitepaper.roadmap.phase1.item3'),
      ],
      status: 'current',
    },
    {
      phase: 'Q2 2026',
      title: t('whitepaper.roadmap.phase2.title'),
      items: [
        t('whitepaper.roadmap.phase2.item1'),
        t('whitepaper.roadmap.phase2.item2'),
        t('whitepaper.roadmap.phase2.item3'),
      ],
      status: 'upcoming',
    },
    {
      phase: 'Q3 2026',
      title: t('whitepaper.roadmap.phase3.title'),
      items: [
        t('whitepaper.roadmap.phase3.item1'),
        t('whitepaper.roadmap.phase3.item2'),
        t('whitepaper.roadmap.phase3.item3'),
      ],
      status: 'upcoming',
    },
    {
      phase: 'Q4 2026',
      title: t('whitepaper.roadmap.phase4.title'),
      items: [
        t('whitepaper.roadmap.phase4.item1'),
        t('whitepaper.roadmap.phase4.item2'),
        t('whitepaper.roadmap.phase4.item3'),
      ],
      status: 'upcoming',
    },
  ];

  return (
    <section id="roadmap">
      <SectionHeader
        icon={<ArrowRight className="h-6 w-6" />}
        title={t('whitepaper.roadmap.title')}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {phases.map((phase) => (
          <div
            key={phase.phase}
            className={`rounded-xl p-5 border ${
              phase.status === 'current'
                ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-300 dark:border-primary-700'
                : 'bg-white dark:bg-secondary-800 border-secondary-200 dark:border-secondary-700'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <span
                className={`text-sm font-semibold ${
                  phase.status === 'current'
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-secondary-500 dark:text-secondary-400'
                }`}
              >
                {phase.phase}
              </span>
              {phase.status === 'current' && (
                <span className="px-2 py-1 bg-primary-500 text-white text-xs font-medium rounded-full">
                  {t('whitepaper.roadmap.current')}
                </span>
              )}
            </div>
            <h4 className="font-semibold text-secondary-900 dark:text-white mb-3">{phase.title}</h4>
            <ul className="space-y-2">
              {phase.items.map((item, itemIndex) => (
                <li
                  key={`${phase.phase}-item-${itemIndex}`}
                  className="flex items-start gap-2 text-sm text-secondary-600 dark:text-secondary-400"
                >
                  <CheckCircle2 className="h-4 w-4 text-primary-500 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function FAQSection() {
  const { t } = useTranslation();

  const faqs = [
    { question: t('whitepaper.faq.q1.question'), answer: t('whitepaper.faq.q1.answer') },
    { question: t('whitepaper.faq.q2.question'), answer: t('whitepaper.faq.q2.answer') },
    { question: t('whitepaper.faq.q3.question'), answer: t('whitepaper.faq.q3.answer') },
    { question: t('whitepaper.faq.q4.question'), answer: t('whitepaper.faq.q4.answer') },
    { question: t('whitepaper.faq.q5.question'), answer: t('whitepaper.faq.q5.answer') },
    { question: t('whitepaper.faq.q6.question'), answer: t('whitepaper.faq.q6.answer') },
  ];

  return (
    <section id="faq">
      <SectionHeader icon={<FileText className="h-6 w-6" />} title={t('whitepaper.faq.title')} />

      <div className="space-y-3">
        {faqs.map((faq) => (
          <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
        ))}
      </div>
    </section>
  );
}

function DisclaimerSection() {
  const { t } = useTranslation();

  return (
    <section
      id="disclaimer"
      className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-xl p-6"
    >
      <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-400 mb-3">
        {t('whitepaper.disclaimer.title')}
      </h3>
      <div className="text-sm text-amber-700 dark:text-amber-300/80 space-y-2">
        <p>{t('whitepaper.disclaimer.p1')}</p>
        <p>{t('whitepaper.disclaimer.p2')}</p>
        <p>{t('whitepaper.disclaimer.p3')}</p>
      </div>
    </section>
  );
}

// ============================================================================
// Helper Components
// ============================================================================

function SectionHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg text-primary-600 dark:text-primary-400">
        {icon}
      </div>
      <h2 className="text-2xl font-bold text-secondary-900 dark:text-white">{title}</h2>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white dark:bg-secondary-800 rounded-xl p-4 text-center border border-secondary-200 dark:border-secondary-700">
      <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">{label}</p>
      <p className="font-bold text-secondary-900 dark:text-white">{value}</p>
    </div>
  );
}

function TokenomicsPoint({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex gap-4 p-4 bg-white dark:bg-secondary-800 rounded-xl border border-secondary-200 dark:border-secondary-700">
      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary-100 dark:bg-secondary-700 flex items-center justify-center">
        {icon}
      </div>
      <div>
        <h4 className="font-semibold text-secondary-900 dark:text-white mb-1">{title}</h4>
        <p className="text-sm text-secondary-600 dark:text-secondary-400">{description}</p>
      </div>
    </div>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-secondary-200 dark:border-secondary-700 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-4 sm:px-6 py-4 text-left flex items-center justify-between bg-white dark:bg-secondary-800 hover:bg-secondary-50 dark:hover:bg-secondary-750 transition-colors min-h-[44px]"
      >
        <span className="font-medium text-secondary-900 dark:text-white pr-4">{question}</span>
        <ChevronDown
          className={`h-5 w-5 text-secondary-400 flex-shrink-0 transform transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-4 sm:px-6 py-4 bg-secondary-50 dark:bg-secondary-850 text-secondary-600 dark:text-secondary-400">
          {answer}
        </div>
      )}
    </div>
  );
}

export default WhitepaperPage;
